import { Component } from '@angular/core'
import { ActivatedRoute, RouterLink } from '@angular/router'
import { FormsModule } from '@angular/forms'
import { TranslateService } from '@ngx-translate/core'
import {
  NgbModal,
  NgbNav,
  NgbNavContent,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap'

import {
  APIToken,
  ElementsUser,
  ElementsUserProfile,
  MCPSession,
  MCPUserKey,
  ParametersOtpPolicyEnum,
} from 'elements-sdk'
import { APIService } from 'api/services/api.service'
import { Mutable } from 'common/helpers'
import { MessageBoxService } from 'common/util/services/messageBox.service'
import { NotifyService } from 'common/util/services/notify.service'
import { LocaleService, TranslatePipe } from 'common/locale'
import { ModalService } from 'common/util.modals'
import { PageTitleComponent } from 'common/util/components/pageTitle.component'
import { TranslateDirective } from 'common/locale/directives/translate.directive'
import { AvatarComponent } from 'common/util/components/avatar.component'
import { SRLabelDirective } from 'common/util/directives/label.directive'
import { BetterSelectComponent } from 'common/util/components/betterSelect.component'
import { FloatingToolbarComponent } from 'common/util/components/floatingToolbar.component'
import { SaveButtonComponent } from 'common/util/components/saveButton.component'
import { EntityListComponent } from 'common/entityList/components/entityList.component'
import { EntityListItemDirective } from 'common/entityList/components/entityListItems.component'
import { AutoselectDirective } from 'common/util/directives/autoselect.directive'
import { CopyToClipboardButtonDirective } from 'common/util/directives/copyToClipboardButton.directive'
import { BadgeModule } from 'common/util.badge'
import { IdentityService, KnownPage } from 'main/services/identity.service'
import { BackendService } from 'main/services/backend.service'
import { ImageCropperModalComponent } from 'main/components/imageCropperModal.component'
import { ParametersService } from 'main/services/parameters.service'
import { ChangePasswordModalComponent } from 'auth'
import { BaseReusableViewComponent, baseReusableViewProviders } from 'root/routeReuseStrategy'
import { UserTotpSetupButtonComponent } from 'main.users/components/userTotpSetupButton.component'
import { NotificationSettingsComponent } from 'notification/components/notificationSettings.component'
import { McpManagementComponent } from 'main.profile/components/mcpManagement.component'

enum ProfileViewTabEnum {
  profile = 'profile',
  notifications = 'notifications',
  api = 'api',
  agents = 'agents',
}

@Component({
  selector: 'profile-view',
  templateUrl: './profileView.component.html',
  styleUrls: ['./profileView.component.scss'],
  providers: baseReusableViewProviders(),
  imports: [
    PageTitleComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    TranslateDirective,
    NgbNavContent,
    FormsModule,
    AvatarComponent,
    SRLabelDirective,
    UserTotpSetupButtonComponent,
    BetterSelectComponent,
    FloatingToolbarComponent,
    SaveButtonComponent,
    NotificationSettingsComponent,
    RouterLink,
    EntityListComponent,
    EntityListItemDirective,
    AutoselectDirective,
    CopyToClipboardButtonDirective,
    NgbNavOutlet,
    TranslatePipe,
    BadgeModule,
    McpManagementComponent,
  ],
})
export class ProfileViewComponent extends BaseReusableViewComponent {
  user: Mutable<ElementsUserProfile>

  tokens: APIToken[] = []
  // `token` is the single base32 connection code (instance id + key bundled).
  mcpKey: MCPUserKey | null = null
  mcpSessions: MCPSession[] = []
  availablePages: KnownPage[] = []
  languages: typeof this.locale.allLanguages

  avatarVersion = 0

  protected ProfileViewTabEnum = ProfileViewTabEnum

  constructor (
    private route: ActivatedRoute,
    private api: APIService,
    protected identity: IdentityService,
    protected parameters: ParametersService,
    private translate: TranslateService,
    private messagebox: MessageBoxService,
    private notify: NotifyService,
    private backend: BackendService,
    private ngbModal: NgbModal,
    private readonly modal: ModalService,
    private locale: LocaleService,
  ) {
    super()

    this.user = this.route.snapshot.data['user']
    this.languages = this.locale.allLanguages

    this.api.auth.getAllAPITokens().then(tokens => {
      this.tokens = tokens
    })
    if (this.parameters.values.mcpTunnelEnabled) {
      this.api.main.getMyMcpKey().then(key => {
        this.mcpKey = key
      })
      this.api.main.getMyMcpSessions().then(sessions => {
        this.mcpSessions = sessions
      })
    }
    this.availablePages = this.identity.getAvailablePages(this.user as unknown as ElementsUser)
  }

  get totpChangesAllowed (): boolean {
    return this.identity.hasPermission('users:manage') || this.parameters.values.otpPolicy !== ParametersOtpPolicyEnum.AdminOnly
  }

  save = async () => {
    try {
      await this.api.main.patchProfile({
        elementsUserProfilePartialUpdate: this.user,
      })
      this.backend.apply()
    } catch (e) {
      this.notify.errorFromResponse(this.translate.instant('Error occurred while saving'), e)
      throw e
    }
  }

  async removeAvatar () {
    await this.api.main.deleteMyAvatar()
    await this.setAvatar(null)
  }

  async changeAvatar () {
    const modal = this.ngbModal.open(ImageCropperModalComponent)
    modal.componentInstance.options.roundCropper = true
    modal.componentInstance.options.altText = this.translate.instant('Avatar preview')
    const image = await modal.result.catch(() => null)
    if (!image) {
      return
    }
    await this.api.main.updateMyAvatar({
      imageUploadRequest: {
        data: image,
      },
    })
    this.avatarVersion = Date.now()
    this.setAvatar(`/api/2/users/${this.user.id}/avatar`)
  }

  private async setAvatar (newAvatar: string | null) {
    if (this.user.avatar === newAvatar) {
      return
    }
    this.user.avatar = newAvatar
  }

  async changePassword () {
    const result = await this.modal.open(ChangePasswordModalComponent, { user: this.user as any })

    if (!result) {
      return
    }

    try {
      await this.api.main.setMyPassword({
        changeOwnPasswordRequest: {
          password: result.password,
          currentPassword: result.currentPassword,
          currentOtp: result.currentOTP,
        },
      })
    } catch (response) {
      this.notify.errorFromResponse(this.translate.instant('Password change failed'), response)
      return
    }

    this.notify.success(this.translate.instant('Password successfully updated'))
  }

  async createToken () {
    const message = await this.messagebox.prompt({ prompt: this.translate.instant('Name for the new token') }).catch(() => null)
    if (!message?.value) {
      return
    }
    const token = await this.api.auth.createAPIToken({
      aPITokenWithSecretUpdate: {
        name: message.value,
      },
    })
    this.tokens = [...this.tokens, token]
  }

  async deleteToken (token: APIToken) {
    this.tokens = this.tokens.filter(x => x !== token)
    await this.api.auth.deleteAPIToken(token)
  }

  async rotateMcpKey () {
    this.mcpKey = await this.api.main.rotateMyMcpKey()
    this.notify.success(this.translate.instant('MCP connection code regenerated'))
  }

  async revokeMcpSession (session: MCPSession) {
    this.mcpSessions = this.mcpSessions.filter(s => s !== session)
    await this.api.main.revokeMyMcpSession({ sessionId: String(session.id) })
  }
}
